import { useState, useEffect, useRef } from 'react';
import { useMediaQuery } from 'react-responsive'
import { useRouter } from 'next/router'

import { useArticleFiltersDrawerContext } from '@/context/ArticleFiltersDrawerContext'
import ArticleSplitHero from '@/components/Article/ArticleSplitHero'
import FullBleedHero from '@/components/Sections/FullBleedHero'
import ArticleRow from '@/components/Sections/ArticleRow'
import DynamicArticleCard from '@/components/Cards/DynamicArticleCard'
import BlogFilters from '@/components/Blog/BlogFilters'
import IconFilters from '@/svgs/filters.svg'
import ArticleCookingClassHero from '@/components/Article/ArticleCookingClassHero'

import IconSearch from '@/svgs/search.svg'
import PaginationLeft from '@/svgs/pagination-left.svg'
import PaginationRight from '@/svgs/pagination-right.svg'

import PageSEO from '@/components/SEO/PageSEO'
import StructuredData from '@/components/SEO/StructuredData'

import classes from "./ListingsTemplate.module.scss"

const ListingsTemplate = ({ articles, blogSettings, page }) => {
  const router = useRouter()
  const drawerContext = useArticleFiltersDrawerContext()
  const { addFilters, openDrawer, isOpen, setSortMethod, selectedFilters, sortListings, addOriginalListings, listings, addTagCount, originalListings, clearFilters } = drawerContext

  const { content, filterGroups } = page.fields
  const heroSection = content?.find(section => section._type === 'hero')
  const articleRowSection = content?.find(section => section._type === 'articleRow')

  const [pages, setPages] = useState(Math.ceil(articles.length / 20))
  const [currentPage, setCurrentPage] = useState(1)
  const [filterDrawer, toggleFilterDrawer]= useState(false)
  const [mounted, setMounted]= useState(false)
  const [searchTerm, setSearchTerm] = useState('')

  const isDesktop = useMediaQuery({query: '(min-width: 1074px)'})

  const { hero } = page?.fields
  const blogType = page?.fields?.blogType
  const blogGlobalSettings = blogSettings ? { ...blogSettings.fields[blogType], blogType} : undefined

  hero.header = page.title
  hero.subheader = page.fields?.subheader

  const listRef = useRef(null)

  useEffect(() => {
    setMounted(true)
    addOriginalListings(articles)
    sortListings(articles, true)
  }, [articles])

  useEffect(() => {
    if(isDesktop && mounted && filterGroups?.length === 0) {
      toggleFilterDrawer(false)
    }

    const tagCount = {}
    originalListings.map((article) => {
      article.fields?.articleTags?.map((tag) => {
        if(!tagCount[tag.value.toLowerCase()]) {
          tagCount[tag.value.toLowerCase()] = 1
        }

        if(tagCount[tag.value.toLowerCase()]) {
          tagCount[tag.value.toLowerCase()]++
        }
      })
    })

    const filterGroupObj = {}
    const multipleSelectedFilters = {}

    filterGroups?.forEach((group) => {
      multipleSelectedFilters[group.title.toLowerCase()] = []

      filterGroupObj[group.title.toLowerCase()] = {
        options: {}
      }

      group.filterOptions?.map((option) => {
        filterGroupObj[group.title.toLowerCase()].options[option.value.toLowerCase()] = {
          checked: false,
          subFilters: {}
        }

        option.subFilters?.map((subFilter) => {
          if(tagCount[subFilter.value?.toLowerCase()] >= 4) {
            filterGroupObj[group.title.toLowerCase()].options[option.value.toLowerCase()].subFilters[subFilter.value.toLowerCase()] = {
              checked: false
            }
          }
        })
      })
    })

    // TODO: Why are we passing in this tag count? Why don't we just filter them out before we pass them to the filtering context?
    addTagCount(tagCount)
    addFilters(filterGroupObj)
  }, [originalListings, filterGroups])

  useEffect(() => {
    if(selectedFilters.length > 0) {
      setCurrentPage(1)
    }
    setPages(Math.ceil(listings.length / 20))
  }, [listings, selectedFilters])

  const getPaginatedData = () => {
    const startIndex = currentPage * 20 - 19
    const endIndex = startIndex + 20
    return listings.slice(startIndex, endIndex)
  }

  const getPaginationGroup = () => {
    let start = Math.floor((currentPage - 1) / pages) * pages
    return new Array(pages).fill().map((_, idx) => start + idx + 1)
  }

  const scrollToTop = () => {
    if (listRef && listRef.current) {
      listRef.current.scrollIntoView()
    }
  }

  const goToNextPage = () => {
    setCurrentPage((page) => page + 1)
    scrollToTop()
  }

  const goToPreviousPage = () => {
    setCurrentPage((page) => page - 1)
    scrollToTop()
  }

  const changePage = (event) => {
    const pageNumber = Number(event.target.textContent)
    setCurrentPage(pageNumber)
    scrollToTop()
  }

  const handleChange = (e) => {
    setSearchTerm(e.target.value)
  }

  const handleKeyDown = (e) => {
    let index
    if(blogType === 'culinary') {
      index = 'culinary_articles'
    } else {
      index = 'brand_articles'
    }

    if (e.key === 'Enter') {
      e.preventDefault()
      router.push(`/pages/search?query=${searchTerm}&index=${index}`)
    }
  }

  return (
    <div className="category-listing-page">
      <StructuredData type="blog" data={page} />
      <PageSEO seo={page.fields.seo} />
      {page.type === "cookingClassCategory" ? (
        <ArticleCookingClassHero fields={page} />
      ):(
        <ArticleSplitHero fields={hero} renderType="blog-listing" blogType={blogType} blogGlobalSettings={blogGlobalSettings} />
      )}
      <div ref={listRef} className={classes["recipes"]}>
        <form className={`${classes['recipes__filter-wrap']} container`}>
          <div className={classes['recipes__search']}>
            <button type="button">
                <IconSearch />
            </button>
            <input type="text" placeholder='Search' className="body" onKeyDown={(e) => handleKeyDown(e)} onChange={(e) => handleChange(e)} value={searchTerm} />
          </div>

          <div className={classes['recipes__filter-row']}>
            {filterGroups && filterGroups?.length > 0 &&
              <>
                <button onClick={clearFilters} type="button" className={`${classes['toggle-filters']} ${classes['desktop']}`}>
                  <span className='body'>Clear Filters</span>
                </button>
                <button onClick={clearFilters} type="button" className={`${classes['toggle-filters']} ${classes['mobile']}`}>
                  <span className='body'>Clear Filters</span>
                </button>
                <button onClick={() => toggleFilterDrawer(!filterDrawer)} type="button" className={`${classes['toggle-filters']} ${classes['desktop']}`}>
                  {filterDrawer ? <span className="body">Hide Filters <IconFilters/></span> : <span className="body">Show Filters <IconFilters/></span>}
                </button>
                <button onClick={() => openDrawer()} type="button" className={`${classes['toggle-filters']} ${classes['mobile']}`}>
                  {isOpen ? <span className="body">Hide Filters <IconFilters/></span> : <span className="body">Show Filters <IconFilters/></span>}
                </button>
              </>
            }

            <div className={classes['sort-by']}>
              <label className="body">Sort By</label>
              <select className="body" onChange={(e) => setSortMethod(e.target.value)}>
                <option value="newest">Newest</option>
                <option value="oldest">Oldest</option>
              </select>
            </div>
          </div>
        </form>

        <div className={`${classes['filters-list__wrap']} ${filterDrawer ? 'open container' : 'close'}`}>
          {filterDrawer && filterGroups?.length > 0 && <div className={`${classes['filters']}`}>
            <BlogFilters />
          </div>}

          {/* TODO: Clean up this weird multiples of 8 crap. It causes the recipes to load oddly on the first page. */}
          {!articles.length ? (
            <div className={`${classes['recipes__list-loading']}`}>
              <h2>Loading Articles...</h2>
            </div>
          ):(

          <div className={`${classes['recipes__list-wrap']} ${classes[filterDrawer && filterGroups ? 'filters-open' : '']} ${filterDrawer && filterGroups ? 'listing-pages--filters-open' : ''}`}>
            {listings.length === 0 && <div className={`${filterDrawer ? '' : 'container'}`}>
              <p className={classes['no-result-text']}>There are no results that meet all of your selected criteria. Try removing a filter to see results.</p>
              <button onClick={clearFilters} type="button" className={`${classes['toggle-filters']} ${classes['desktop']}`}>
                <span className='body'>Clear Filters</span>
              </button>
            </div>}

            {listings.length > 0  && currentPage === 1 && selectedFilters.length === 0 && <div className={`${classes['recipes__list']} ${filterDrawer ? '' : 'container'} ${classes[filterDrawer && filterGroups ? 'filters-open' : '']}`}>
              {listings.map((article) => {
                  return (
                    <div className={classes['grid-item']} key={article.handle}>
                      <DynamicArticleCard article={article} />
                    </div>
                  )
              })}
            </div>}


            {/* {listings.length > 0 && currentPage === 1 && selectedFilters.length === 0 && <div className={`${classes['recipes__list']} ${filterDrawer ? '' : 'container'} ${classes[filterDrawer && filterGroups ? 'filters-open' : '']}`}>
                  {listings.slice(0, 8).map((article) => {
                    return (
                      <div className={classes['grid-item']} key={article.handle}>
                          <DynamicArticleCard article={article} />
                      </div>
                    )
              })}
            </div>}

            {listings.length >= 8 && currentPage === 1 && selectedFilters.length === 0 && heroSection &&
              <FullBleedHero fields={heroSection} key={heroSection._key} />
            }

            {listings.length >= 8 && currentPage === 1 && selectedFilters.length === 0 && <div className={`${classes['recipes__list']} ${filterDrawer ? '' : 'container'} ${classes[filterDrawer && filterGroups ? 'filters-open' : '']}`}>
              {listings.slice(8, 16).map((article) => {
                  return (
                    <div className={classes['grid-item']} key={article.handle}>
                      <DynamicArticleCard article={article} />
                    </div>
                  )
              })}
            </div>}

            {listings.length >= 17 && currentPage === 1 && selectedFilters.length === 0 && articleRowSection &&
              <div>
                <ArticleRow fields={articleRowSection} />
              </div>
            }

            {listings.length >= 16 && currentPage === 1 && selectedFilters.length === 0 && <div className={`${classes['recipes__list']} ${filterDrawer ? '' : 'container'} ${classes[filterDrawer && filterGroups ? 'filters-open' : '']}`}>
              {listings.slice(16, 21).map((article) => {
                  return (
                    <div className={classes['grid-item']} key={article.handle}>
                        <DynamicArticleCard article={article} />
                    </div>
                  )
              })}
            </div>} */}

            {currentPage !== 1 && <div className={`${classes['recipes__list']} ${filterDrawer ? '' : 'container'} ${classes[filterDrawer && filterGroups ? 'filters-open' : '']}`}>
              {getPaginatedData().map((article) => (
                  <div className={classes['grid-item']} key={article.handle}>
                    <DynamicArticleCard article={article}  />
                </div>
              ))}
            </div>}

            {selectedFilters.length > 0 && <div className={`${classes['recipes__list']} ${filterDrawer ? '' : 'container'} ${classes[filterDrawer && filterGroups ? 'filters-open' : '']}`}>
              {listings.map((article) => (
                <div className={classes['grid-item']} key={article.handle}>
                  <DynamicArticleCard article={article} />
                </div>
              ))}
            </div>}
          </div>
        )}

      </div>

        {selectedFilters.length === 0 && <div className={classes['pagination']}>
          <button
            onClick={goToPreviousPage}
            className={`${classes['prev']} ${classes[currentPage === 1 ? 'disabled' : '']} pagination-btn`}
          >
            <PaginationLeft />
          </button>

            {getPaginationGroup().map((item, index) => (
              <button
                key={index}
                onClick={changePage}
                className={`${classes['paginationItem']} ${classes[currentPage === item ? 'active' : null]}`}
              >
                <span>{item}</span>
              </button>
            ))}

            <button
              onClick={goToNextPage}
              className={`${classes['next']} ${classes[currentPage === pages ? 'disabled' : '']} pagination-btn`}
            >
              <PaginationRight />
            </button>
          </div>}
        </div>
    </div>
  )
}

export default ListingsTemplate
